import React, { useEffect, useState, useMemo } from 'react';
import { transformData } from '../../super-admin/utils/transformData';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getMachineryList,
  addMachinery,
  handleCheckMachineDelete,
  deleteMachine,
  updateMachinery
} from '../services/api/implementation/impl';
import { useForm, useWatch } from 'react-hook-form';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import DateFormat from '../../super-admin/utils/DateFormat';
import toast from 'react-hot-toast';
import FormInput from '../../shared/components/ui/FormInput';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Header from '../../shared/components/layout/Header';
import Layout from '../components/layout/AppLayout';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import Loader from '../../shared/components/ui/Loader';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import DeleteIcon from '../../../modules/shared/assets/svg/delete.svg';
import EditIcon from '../../../modules/shared/assets/svg/edit.svg';
import { Plus } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { multipleUploadFiles } from '../features/files';

const MachineryList = () => {
  const userId = Cookies.get('userId');
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    control,
    setValue
  } = useForm();
  const { collapsed } = useSidebar();
  const dispatch = useDispatch();
  const multifiles = useSelector((state) => state.file.multifiles);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [machines, setMachines] = useState([
    {
      name: '',
      type: '',
      ownerType: '',
      fuelType: '',
      consumptionPerHour: '',
      rentCostType: '',
      totalCostPerHour: '',
      fuelIncluded: '',
      fuelCost: '',
      numberPlate: '',
      docs: []
    }
  ]);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page

  const {
    data: Machinery,
    refetch: refetchMachinery,
    isFetching: isFetchingMachinery
  } = useQuery({
    queryKey: ['machinery', page],
    queryFn: () => {
      return getMachineryList(page, limit);
    },
    keepPreviousData: true
  });

  const totalPages = Machinery?.meta ? Math.ceil(Machinery?.meta.total / limit) : 1;

  // Dynamically watch all fuelType, ownerType, and fuelIncluded values
  const allWatchedFields = useWatch({ control });

  const getWatchedValue = (idx, key) =>
    allWatchedFields?.[`machine_${idx}_${key}`] ?? machines[idx]?.[key] ?? '';

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Track previous values to avoid unnecessary resets
  const prevValues = useMemo(() => {
    return machines.map((_, idx) => ({
      fuelType: getWatchedValue(idx, 'fuelType'),
      ownerType: getWatchedValue(idx, 'ownerType'),
      fuelIncluded: getWatchedValue(idx, 'fuelIncluded')
    }));
  }, [allWatchedFields, machines.length]);

  // Effect to reset fields based on form value changes
  useEffect(() => {
    if (!allWatchedFields) return;

    machines.forEach((_, idx) => {
      const fuelType = getWatchedValue(idx, 'fuelType');
      const ownerType = getWatchedValue(idx, 'ownerType');
      const fuelIncluded = getWatchedValue(idx, 'fuelIncluded');

      const prev = prevValues[idx];

      // Only reset if values actually changed
      if (fuelType !== prev.fuelType) {
        if (fuelType === 'NA') {
          setValue(`machine_${idx}_consumptionPerHour`, '');
        }
        if (['NA', 'Electricity'].includes(fuelType)) {
          setValue(`machine_${idx}_fuelIncluded`, '');
          setValue(`machine_${idx}_fuelCost`, '');
        }
      }

      if (ownerType !== prev.ownerType) {
        if (ownerType && ownerType !== 'Rental') {
          setValue(`machine_${idx}_totalCostPerHour`, '');
          setValue(`machine_${idx}_fuelIncluded`, '');
          setValue(`machine_${idx}_fuelCost`, '');
          setValue(`machine_${idx}_rentCostType`, '');
        } else if (ownerType === 'Rental') {
          // Set default rent cost type to Hourly when owner type becomes Rental
          const currentRentCostType = getWatchedValue(idx, 'rentCostType');
          if (!currentRentCostType || currentRentCostType === '') {
            setValue(`machine_${idx}_rentCostType`, 'Hourly');
            handleMachineChange(idx, 'rentCostType', 'Hourly');
          }
        }
      }

      if (fuelIncluded !== prev.fuelIncluded) {
        if (fuelIncluded === 'true') {
          setValue(`machine_${idx}_fuelCost`, '');
        }
      }
    });
  }, [allWatchedFields, setValue, machines.length, prevValues]);

  // Add Machinery Modal Functions
  const handleAddMachinery = () => {
    // Reset form when opening modal
    reset();
    setMachines([
      {
        name: '',
        type: '',
        ownerType: '',
        fuelType: '',
        consumptionPerHour: '',
        rentCostType: '',
        totalCostPerHour: '',
        fuelIncluded: '',
        fuelCost: '',
        numberPlate: '',
        docs: []
      }
    ]);
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setIsEditMode(false);
    setEditingMachine(null);
    setMachines([
      {
        name: '',
        type: '',
        ownerType: '',
        fuelType: '',
        consumptionPerHour: '',
        rentCostType: '',
        totalCostPerHour: '',
        fuelIncluded: '',
        fuelCost: '',
        numberPlate: '',
        docs: []
      }
    ]);
    // Reset the form
    reset();
  };

  const handleMachineChange = (index, field, value) => {
    console.log('handleMachineChange:', { index, field, value });
    const newMachines = [...machines];
    newMachines[index][field] = value;
    setMachines(newMachines);
  };

  // Create register functions for each machine field

  const handleAddMachineRow = () => {
    setMachines([
      ...machines,
      {
        name: '',
        type: '',
        ownerType: '',
        fuelType: '',
        consumptionPerHour: '',
        rentCostType: '',
        totalCostPerHour: '',
        fuelIncluded: '',
        fuelCost: '',
        numberPlate: '',
        docs: []
      }
    ]);
  };

  const handleRemoveMachineRow = (index) => {
    if (machines.length > 1) {
      const newMachines = machines.filter((_, i) => i !== index);
      setMachines(newMachines);
    }
  };

  const handleEditMachine = (machine) => {
    setIsEditMode(true);
    setEditingMachine(machine);
    setIsAddModalOpen(true);
    // Pre-populate the form with machine data
    // Extract only the numeric value from consumption (remove unit)
    const consumptionValue = machine.consumptionPerHour
      ? machine.consumptionPerHour.toString().split(' ')[0]
      : '';

    setMachines([
      {
        name: machine.name || '',
        type: machine.type || '',
        ownerType: machine.ownerType || '',
        fuelType: machine.fuelType || '',
        consumptionPerHour: consumptionValue,
        rentCostType: machine.rentCostType || '', // Don't default here
        totalCostPerHour: machine.totalCostPerHour || '',
        fuelIncluded: machine.fuelIncluded ? 'true' : 'false' || '',
        fuelCost: machine.fuelCost || '',
        numberPlate: machine.numberPlate || '',
        docs: machine.docs || []
      }
    ]);

    // Set form values - use setTimeout to ensure dropdown is rendered
    setTimeout(() => {
      setValue('machine_0_name', machine.name || '');
      setValue('machine_0_type', machine.type || '');
      setValue('machine_0_ownerType', machine.ownerType || '');
      setValue('machine_0_fuelType', machine.fuelType || '');
      setValue('machine_0_totalCostPerHour', machine.totalCostPerHour || '');
      setValue('machine_0_fuelCost', machine.fuelCost || '');
      setValue(`machine_0_consumptionPerHour`, consumptionValue);
      setValue(`machine_0_fuelIncluded`, machine.fuelIncluded ? 'true' : 'false');
      setValue('machine_0_numberPlate', machine.numberPlate || '');
      // Set rentCostType last after other fields are set
      if (machine.rentCostType) {
        setValue('machine_0_rentCostType', machine.rentCostType);
      }

      // Dispatch existing documents to Redux for file-secondary component
      if (machine.docs && machine.docs.length > 0) {
        dispatch(
          multipleUploadFiles({
            fileType: 'machine_0_documents',
            urls: machine.docs
          })
        );
      }
    }, 0);
  };

  const handleSubmitMachinery = async (formData) => {
    console.log('🏢 formData', formData);

    // Extract machine data from form
    const machinesData = [];
    for (let i = 0; i < machines.length; i++) {
      const machineCost = formData[`machine_${i}_totalCostPerHour`]
        ? parseFloat(formData[`machine_${i}_totalCostPerHour`])
        : 0;
      const fuelCost = formData[`machine_${i}_fuelCost`]
        ? parseFloat(formData[`machine_${i}_fuelCost`])
        : 0;
      const fuelIncluded =
        formData[`machine_${i}_fuelIncluded`] === 'true'
          ? true
          : formData[`machine_${i}_fuelIncluded`] === 'false'
            ? false
            : null;

      // Calculate total cost per hour
      let totalCostPerHour = 0;
      if (formData[`machine_${i}_ownerType`] === 'Rental') {
        if (fuelIncluded === true) {
          // If fuel is included, only machine cost
          totalCostPerHour = machineCost;
        } else if (fuelIncluded === false) {
          // If fuel is excluded, machine cost + fuel cost
          totalCostPerHour = machineCost + fuelCost;
        } else if (formData[`machine_${i}_fuelType`] === 'Electricity') {
          // For electricity, always add machine cost + fuel cost (unit cost)
          totalCostPerHour = machineCost + fuelCost;
        }
      }

      const machine = {
        name: formData[`machine_${i}_name`] || '',
        type: formData[`machine_${i}_type`] || '',
        ownerType: formData[`machine_${i}_ownerType`] || '',
        fuelType: formData[`machine_${i}_fuelType`] || '',
        numberPlate: formData[`machine_${i}_numberPlate`] || '',
        docs: multifiles[`machine_${i}_documents`] || []
      };

      // Add consumption per hour only if fuel type is not NA
      if (formData[`machine_${i}_fuelType`] !== 'NA') {
        machine.consumptionPerHour = formData[`machine_${i}_consumptionPerHour`]
          ? parseFloat(formData[`machine_${i}_consumptionPerHour`])
          : null;
      }

      // Add rent cost type for rental machines
      if (formData[`machine_${i}_ownerType`] === 'Rental') {
        machine.rentCostType = formData[`machine_${i}_rentCostType`] || 'Hourly';
      }

      // Add fuel cost for Self owner type with non-NA fuel type
      if (
        formData[`machine_${i}_ownerType`] === 'Self' &&
        formData[`machine_${i}_fuelType`] !== 'NA'
      ) {
        machine.fuelCost = fuelCost > 0 ? fuelCost : null;
      }

      // Add rental-specific fields only if owner type is Rental
      if (formData[`machine_${i}_ownerType`] === 'Rental') {
        machine.totalCostPerHour = totalCostPerHour;

        // Add fuel included only for non-NA and non-Electricity fuel types
        if (
          formData[`machine_${i}_fuelType`] !== 'NA' &&
          formData[`machine_${i}_fuelType`] !== 'Electricity'
        ) {
          machine.fuelIncluded = fuelIncluded;

          // Add fuel cost only if fuel is excluded
          if (fuelIncluded === false && fuelCost > 0) {
            machine.fuelCost = fuelCost;
          }
        }
      }

      // Remove null values to match the required payload structure
      Object.keys(machine).forEach((key) => {
        if (machine[key] === null || machine[key] === '') {
          delete machine[key];
        }
      });

      machinesData.push(machine);
    }

    console.log('🏢 machinesData', machinesData);

    // Validate all machines
    for (let i = 0; i < machinesData.length; i++) {
      const machine = machinesData[i];
      if (!machine.name || !machine.type || !machine.ownerType || !machine.fuelType) {
        toast.error('Please fill all required fields for all machines.');
        return;
      }

      // Validate consumption per hour for non-NA fuel types
      if (machine.fuelType !== 'NA' && !machine.consumptionPerHour) {
        toast.error('Consumption per hour is required for non-NA fuel types.');
        return;
      }

      // Validate rental-specific fields
      if (machine.ownerType === 'Rental') {
        if (!machine.rentCostType) {
          toast.error('Rent cost type is required for rental machines.');
          return;
        }

        if (!machine.totalCostPerHour) {
          toast.error('Total cost per hour is required for rental machines.');
          return;
        }

        console.log('🏢 machine', machine?.fuelType, machine?.fuelIncluded);

        // Validate fuel included field for non-NA and non-electricity fuel types
        if (machine.fuelType !== 'NA' && machine.fuelType !== 'Electricity') {
          if (machine.fuelIncluded === null || machine.fuelIncluded === undefined) {
            toast.error('Please specify if fuel is included for rental machines.');
            return;
          }

          // Validate fuel cost if fuel is excluded
          if (machine.fuelIncluded === false && !machine.fuelCost) {
            toast.error('Fuel cost is required when fuel is excluded.');
            return;
          }
        }

        // For electricity, validate that both machine cost and fuel cost are provided
        if (machine.fuelType === 'Electricity') {
          const machineCost = formData[`machine_${i}_totalCostPerHour`]
            ? parseFloat(formData[`machine_${i}_totalCostPerHour`])
            : 0;
          const fuelCost = formData[`machine_${i}_fuelCost`]
            ? parseFloat(formData[`machine_${i}_fuelCost`])
            : 0;

          if (!machineCost) {
            toast.error('Machine cost is required for rental machines with electricity fuel type.');
            return;
          }

          if (!fuelCost) {
            toast.error('Unit cost is required for rental machines with electricity fuel type.');
            return;
          }
        }
      }
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && editingMachine) {
        // Handle edit - update existing machine
        const payload = {
          ...machinesData[0]
        };

        console.log('🏢 edit payload', payload);
        // You'll need to implement updateMachinery API call
        try {
          await updateMachinery(editingMachine.id, payload);
          toast.success('Machinery updated successfully');
        } catch (err) {
          toast.error(err.response?.data?.message || err?.message || 'Error updating machinery');
        }
      } else {
        // Handle add - create new machine
        const payload = {
          machines: machinesData
        };

        console.log('🏢 add payload', payload);
        await addMachinery(payload);
        toast.success('Machinery added successfully');
      }

      // Reset the form after successful submission
      reset();
      handleCloseAddModal();
      refetchMachinery();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err?.message ||
          `Error ${isEditMode ? 'updating' : 'adding'} machinery`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Machines List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const MachineData = useMemo(() => {
    if (!Machinery?.data) return [];

    return Machinery?.data?.map((item) => {
      // Transform the data manually to avoid issues with transformData function
      const transformedItem = {
        id: item.id,
        name: item?.name || '-',
        type: item?.type || '-',
        numberPlate: item?.numberPlate || '-',
        ownerType: item?.ownerType || '-',
        fuelType: item?.fuelType || '-',
        consumptionPerHour: item?.consumptionPerHour
          ? `${item.consumptionPerHour} ${item.fuelType === 'Electricity' ? 'Unit/Hr' : 'Lts/Hr'}`
          : '-',
        totalCostPerHour: item?.totalCostPerHour
          ? `${item.totalCostPerHour.toLocaleString('en-IN')}`
          : '-',
        fuelIncluded:
          item?.fuelIncluded === true ? 'Yes' : item?.fuelIncluded === false ? 'No' : '-',
        fuelCost: item?.fuelCost ? `${item.fuelCost.toLocaleString('en-IN')}` : '-',
        actions: 'Actions', // This will be handled by the table component,
        rentCostType: item?.rentCostType || '-',
        docs: item?.docs || []
      };

      return transformedItem;
    });
  }, [Machinery?.data]);

  useEffect(() => {
    if (MachineData && MachineData.length > 0) {
      setFilteredData(MachineData);
    } else {
      setFilteredData([]);
    }
  }, [MachineData]);

  const [filteredData, setFilteredData] = useState([]);

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckMachineDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const machine = Machinery?.data?.find((item) => parseInt(item.id) === parseInt(id));
      const machineName = machine?.name || 'Machine';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: machineName });
        setSelectedIds([parseInt(id)]);
        setIsSubmitting(false);
      } else {
        // Fallback if not found
        setResData({ ...res, name: machineName });
        setCanDelete('no');
        setIsSubmitting(false);
        setSelectedIds([parseInt(id)]);
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      setIsSubmitting(false);
      toast.error(err.response.data.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteMachine(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      console.log('🗑️ Delete successful, refetching data...');
      setIsSubmitting(false);
      toast.success('Machine Deleted Successfully');
      setSelectedIds([]);
      setSelectedItems([]);
      setCanDelete(null);
      setIsBulk(false);
      setSelectAll(false);
      // Force refetch and update the table
      await refetchMachinery();
      // Clear filtered data to ensure fresh data is loaded
      setFilteredData([]);
      console.log('🔄 Refetch completed');
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err.response.data.message || err?.message || 'Error deleting machine');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const handleSelectAll = async (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      setIsSubmitting(true); // Start loader

      try {
        const promises = Machinery?.data?.map((item) =>
          handleCheckMachineDelete(item.id)
            .then((res) => {
              if (res.canDelete) return item.id;
              return null;
            })
            .catch(() => null)
        );

        const results = await Promise.all(promises);
        const validIds = results.filter((id) => id !== null);
        setSelectedItems(validIds);
      } finally {
        setIsSubmitting(false); // Stop loader
      }
    } else {
      setSelectedItems([]);
    }
  };

  const HandleFunctionCalls = async (id, checked) => {
    if (checked) {
      setIsSubmitting(true);
      try {
        const res = await handleCheckMachineDelete(id);
        if (res.canDelete) {
          setIsSubmitting(false);
          setSelectedItems((prev) => [...prev, id]);
        } else if (!res.canDelete) {
          setIsSubmitting(false);
          toast.error(`Can not delete this machine because there is dependencies`);
        }
      } catch (err) {
        setIsSubmitting(false);
        toast.error(err.response.data.message || err?.message || 'Error checking dependencies');
      }
    } else {
      setIsSubmitting(false);
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    }
  };

  return (
    <>
      <Loader isVisible={isSubmitting || isFetchingMachinery} message={'Loading...'} />
      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this machine'}'`}
          onClose={() => {
            setCanDelete(null);
            setIsBulk(false);
            setSelectAll(false);
            setSelectedItems([]);
            setSelectedIds([]);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this machine has dependencies, please delete requirements of following
              tasks to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {(() => {
                const reasons = Array.isArray(resData?.reason)
                  ? resData.reason
                  : resData?.reason
                    ? [resData.reason]
                    : [];
                return reasons.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < reasons.length - 1 ? ', ' : ''}
                  </span>
                ));
              })()}
            </div>
          </div>
          <div className="flex flex-col justify-start items-center mt-4 w-[100%]  bg-[#f7f4d0] pt-[4px] pb-[4px] pl-2 pr-2 rounded-[4px]">
            <div className="flex justify-between w-[99%] items-center bg-white p-2 pt-1 pb-1">
              <p>Would you like to delete more machines ?</p>
              <div
                onClick={(e) => {
                  setIsBulk(!isBulk);
                  setSelectedItems([]);
                  setSelectAll(false);
                }}
                className="bg-amber-100 p-2 pt-1 pb-1 rounded-[5px] flex justify-center items-center gap-2 cursor-pointer">
                Bulk Delete
                <input
                  type="checkbox"
                  checked={isBulk}
                  readOnly
                  className="outline-none border-none"
                />
              </div>
            </div>

            {isBulk && (
              <div className="flex justify-end w-[100%] pr-[1.2rem] pt-3">
                <div className="flex gap-2">
                  <p>Select All: </p>
                  <input
                    className="outline-none border-none"
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </div>
              </div>
            )}
            {isBulk && (
              <div className="pl-[10px] w-[100%] pr-[19px] mt-2 mb-2 max-h-[14rem] overflow-auto">
                {filteredData &&
                  filteredData?.map((c, index) => (
                    <div key={index} className="flex justify-between items-center text-[#5b5b5b]">
                      {index + 1}. {c.name}
                      <input
                        className="outline-none border-none"
                        type="checkbox"
                        checked={selectedItems?.includes(c.id)}
                        disabled={selectedIds?.includes(c.id)} // Disable if already selected
                        onChange={(e) => HandleFunctionCalls(c.id, e.target.checked)}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="reset"
              onClick={() => {
                setCanDelete(null);
                setIsBulk(false);
                setSelectAll(false);
                setSelectedItems([]);
                setSelectedIds([]);
              }}
              displayType="secondary">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                HandleDelete(selectedItems);
              }}
              disabled={isSubmitting || selectedItems.length === 0}
              displayType="primary-red">
              Delete
            </Button>
          </div>
        </Modal>
      )}
      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'machine'}
            onClose={() => setCanDelete(null)}
            data={filteredData}
            type={'machine'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}
      {/* Main content */}
      <Layout>
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `Machinery/Equipment List` }} />
        </div>

        {/* <div className="flex z-10 items-center justify-between px-6 py-4 bg-white fixed top-[9rem] border-t-[2px] border-stone-100 left-[18vw] w-[82vw] shadow-sm"> */}
        {/* Floating Add Button */}
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={handleAddMachinery}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>

        <GridWrapper width={100}>
          <div className="mt-[5.5rem]  relative h-[calc(100vh-7rem)]  overflow-auto rounded-t-lg flex flex-col items-center gap-4">
            <div className="overflow-x-auto  w-full relative rounded-t-lg">
              <table className="text-left w-full min-w-[1300px]">
                <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                  <tr className="font-normal h-[3rem] w-full whitespace-nowrap">
                    <th className="px-4 py-2 border-b font-normal">Machine Name</th>
                    <th className="px-4 py-2 border-b font-normal">Type</th>
                    <th className="px-4 py-2 border-b font-normal">Number Plate</th>
                    <th className="px-4 py-2 border-b font-normal">Owner Type</th>
                    <th className="px-4 py-2 border-b font-normal">Fuel Type</th>
                    <th className="px-4 py-2 border-b font-normal">Consumption Per Hour</th>
                    <th className="px-4 py-2 border-b font-normal">Total Cost Per Hour</th>
                    <th className="px-4 py-2 border-b font-normal">Fuel Included</th>
                    <th className="px-4 py-2 border-b font-normal">Fuel Cost</th>
                    <th className="px-4 py-2 border-b font-normal min-w-[120px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData && filteredData?.length > 0 ? (
                    filteredData?.map((machine, index) => {
                      return (
                        <React.Fragment key={machine?.id || index}>
                          <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                            <td className="px-4 py-2 text-sm">{machine?.name || '---'}</td>
                            <td className="px-4 py-2 text-sm">{machine?.type || '---'}</td>
                            <td className="px-4 py-2 text-sm">{machine?.numberPlate || '---'}</td>
                            <td className="px-4 py-2 text-sm">{machine?.ownerType || '---'}</td>
                            <td className="px-4 py-2 text-sm">{machine?.fuelType || '---'}</td>
                            <td className="px-4 py-2 text-sm">
                              {machine?.consumptionPerHour || '---'}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {machine?.totalCostPerHour || '---'}
                            </td>
                            <td className="px-4 py-2 text-sm">{machine?.fuelIncluded || '---'}</td>
                            <td className="px-4 py-2 text-sm">{machine?.fuelCost || '---'}</td>
                            <td className="px-4 py-2 text-sm min-w-[120px]">
                              <div className="flex items-center justify-start w-full relative gap-2 flex-shrink-0">
                                <button
                                  className="cursor-pointer flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditMachine(machine);
                                  }}>
                                  <img
                                    src={EditIcon}
                                    alt=""
                                    className="w-6 h-6 cursor-pointer flex-shrink-0"
                                  />
                                </button>
                                <button
                                  className="cursor-pointer flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckDelete(machine?.id);
                                  }}>
                                  <img
                                    src={DeleteIcon}
                                    alt=""
                                    className="w-6 h-6 cursor-pointer flex-shrink-0"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center py-10 align-middle">
                        <div className="flex flex-col items-center justify-center w-full h-full">
                          <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                          <p className="text-gray-600 text-sm">
                            The page you are trying to access has no data.
                            <br />
                            Proceed with adding data.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {filteredData && filteredData?.length > 0 && (
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetchingMachinery}
              />
            )}
          </div>
        </GridWrapper>

        {/* Add Machinery Modal */}
        {isAddModalOpen && (
          <Modal
            isDirty={true}
            confirmOnClose={true}
            isOpen={isAddModalOpen}
            width={60}
            name={isEditMode ? 'Edit Machinery' : 'Add Machinery'}
            onClose={handleCloseAddModal}>
            <form onSubmit={handleSubmit(handleSubmitMachinery)} className="space-y-6">
              {/* Machinery List */}
              {machines.map((machine, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-md font-medium text-gray-700">Machine {idx + 1}</h4>
                    {machines.length > 1 && !isEditMode && (
                      <Button
                        type="button"
                        displayType="secondary"
                        onClick={() => handleRemoveMachineRow(idx)}
                        customStyle={{
                          height: '2rem',
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem'
                        }}>
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <FormInput
                      width={32}
                      label="Type"
                      type="dropdown"
                      isRequired
                      options={[
                        { label: 'Select Type', value: '', disabled: true },
                        { label: 'Machine', value: 'Machine' },
                        { label: 'Equipment', value: 'Equipment' }
                      ]}
                      register={register}
                      register_key={`machine_${idx}_type`}
                      onType={(val) => handleMachineChange(idx, 'type', val)}
                    />
                    <FormInput
                      width={32}
                      label="Machine Name"
                      type="text"
                      isRequired
                      placeholder="e.g., JCB, Excavator"
                      valuePre={machines[idx]?.name || ''}
                      register={register}
                      register_key={`machine_${idx}_name`}
                      onType={(val) => handleMachineChange(idx, 'name', val)}
                    />
                    <FormInput
                      width={32}
                      label="Number Plate"
                      type="text"
                      placeholder="e.g., KA01AB1234"
                      valuePre={machines[idx]?.numberPlate || ''}
                      register={register}
                      register_key={`machine_${idx}_numberPlate`}
                      onType={(val) => handleMachineChange(idx, 'numberPlate', val)}
                    />
                    <FormInput
                      width={32}
                      register={register}
                      register_key={`machine_${idx}_documents`}
                      label="Documents"
                      type="file-secondary"
                      accept=".pdf, .png, .jpg, .jpeg, .doc, .docx"
                      prefilledFiles={machines[idx]?.docs || []}
                    />
                    <FormInput
                      width={32}
                      label="Owner Type"
                      type="dropdown"
                      isRequired
                      options={[
                        { label: 'Select Owner Type', value: '', disabled: true },
                        { label: 'Self', value: 'Self' },
                        { label: 'Rental', value: 'Rental' }
                      ]}
                      register={register}
                      register_key={`machine_${idx}_ownerType`}
                      onType={(val) => {
                        console.log('Owner Type changed to:', val);
                        handleMachineChange(idx, 'ownerType', val);
                      }}
                    />
                    <FormInput
                      width={32}
                      label="Fuel Type"
                      type="dropdown"
                      isRequired
                      options={[
                        { label: 'Select Fuel Type', value: '', disabled: true },
                        { label: 'Diesel', value: 'Diesel' },
                        { label: 'Petrol', value: 'Petrol' },
                        { label: 'Electricity', value: 'Electricity' },
                        { label: 'NA', value: 'NA' }
                      ]}
                      register={register}
                      register_key={`machine_${idx}_fuelType`}
                      onType={(val) => handleMachineChange(idx, 'fuelType', val)}
                    />
                    {getWatchedValue(idx, 'fuelType') !== 'NA' && (
                      <div
                        className="flex-shrink-0 flex flex-col gap-2 text-gray-700"
                        style={{ width: '32%' }}>
                        <div className="flex justify-between items-start space-x-2 w-fit">
                          <span className="text-gray-600">Consumption</span>
                          <span className="text-red-500">*</span>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Enter consumption"
                            value={machines[idx]?.consumptionPerHour || ''}
                            {...register(`machine_${idx}_consumptionPerHour`)}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                handleMachineChange(idx, 'consumptionPerHour', value);
                              }
                            }}
                            className="w-full p-2 border-[1px] border-stone-300 rounded pr-16"
                          />

                          <div className="absolute inset-y-0 right-0 flex items-center pl-1 pr-1 pointer-events-none bg-gray-100 rounded-r-md m-[2px]">
                            <span className="text-sm text-gray-500">
                              {getWatchedValue(idx, 'fuelType') === 'Electricity'
                                ? `Unit/Hr`
                                : 'Lts/Hr'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Rental-specific fields */}
                    {getWatchedValue(idx, 'ownerType') === 'Rental' && (
                      <>
                        <FormInput
                          width={32}
                          label="Rent Cost Type"
                          type="dropdown"
                          isRequired
                          options={[
                            { label: 'Hourly', value: 'Hourly' },
                            { label: 'Daily', value: 'Daily' },
                            { label: 'Monthly', value: 'Monthly' }
                          ]}
                          register={register}
                          register_key={`machine_${idx}_rentCostType`}
                          onType={(val) => handleMachineChange(idx, 'rentCostType', val)}
                        />
                        <FormInput
                          width={32}
                          label="Machine Cost"
                          type="number"
                          isRequired
                          placeholder="Enter machine cost"
                          valuePre={machines[idx]?.totalCostPerHour || ''}
                          register={register}
                          register_key={`machine_${idx}_totalCostPerHour`}
                          onType={(val) => handleMachineChange(idx, 'totalCostPerHour', val)}
                        />

                        {getWatchedValue(idx, 'fuelType') &&
                          getWatchedValue(idx, 'fuelType') !== 'NA' &&
                          getWatchedValue(idx, 'fuelType') !== 'Electricity' && (
                            <div className="flex flex-col gap-2" style={{ width: '32%' }}>
                              <label className="text-sm font-medium text-gray-700">
                                Fuel Included <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-[3rem]">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    {...register(`machine_${idx}_fuelIncluded`)}
                                    value="true"
                                    checked={machines[idx]?.fuelIncluded === 'true'}
                                    onChange={(e) => {
                                      handleMachineChange(idx, 'fuelIncluded', e.target.value);
                                      // Reset fuel cost when fuel is included
                                      handleMachineChange(idx, 'fuelCost', '');
                                    }}
                                    className="text-blue-600"
                                  />
                                  <span className="text-sm text-gray-700">Yes</span>
                                </label>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    {...register(`machine_${idx}_fuelIncluded`)}
                                    value="false"
                                    checked={machines[idx]?.fuelIncluded === 'false'}
                                    onChange={(e) => {
                                      handleMachineChange(idx, 'fuelIncluded', e.target.value);
                                    }}
                                    className="text-blue-600"
                                  />
                                  <span className="text-sm text-gray-700">No</span>
                                </label>
                              </div>
                            </div>
                          )}
                      </>
                    )}
                    {getWatchedValue(idx, 'fuelType') !== 'NA' &&
                      getWatchedValue(idx, 'fuelIncluded') !== 'true' && (
                        <FormInput
                          width={32}
                          label={
                            getWatchedValue(idx, 'fuelType') === 'Electricity'
                              ? 'Unit Cost'
                              : 'Fuel Cost'
                          }
                          type="number"
                          isRequired
                          placeholder={
                            getWatchedValue(idx, 'fuelType') === 'Electricity'
                              ? 'Enter unit cost'
                              : 'Enter fuel cost'
                          }
                          valuePre={machines[idx]?.fuelCost || ''}
                          register={register}
                          register_key={`machine_${idx}_fuelCost`}
                          onType={(val) => handleMachineChange(idx, 'fuelCost', val)}
                        />
                      )}
                  </div>
                </div>
              ))}

              {/* Add More Machine Button - Only show in add mode */}
              {!isEditMode && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    displayType="secondary"
                    onClick={handleAddMachineRow}
                    customStyle={{
                      height: '2.5rem',
                      fontSize: '0.875rem'
                    }}>
                    + Add Another Machine
                  </Button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  displayType="secondary"
                  onClick={handleCloseAddModal}
                  customStyle={{
                    height: '2.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  displayType="primary"
                  disabled={isSubmitting}
                  customStyle={{
                    height: '2.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>
          </Modal>
        )}
      </Layout>
    </>
  );
};

export default MachineryList;
